import { postObjectsFilter } from 'sitemodel/shared/api'

// resolveQuery recebe um objeto no padrão YesQueryLanguage,
// padrão inspirado no MongoDB, e resolve recursivamente todos
// os operadores do tipo $filteraction, se existir
//
// Se não existir nenhum operador do tipo $filteraction
// para ser resolvido, simplesmente devolvemos o mesmo
// queryObject
//
// O método aceita um callback onProgressCallback que é chamado
// a cada nova etapa da resolução dos filteractions,
// para permitir que a UI comunique o progresso ao usuário do sistema
//
// O array fetchedFilteractionIds é utilizado para identificar loops infinitos
export async function resolveQuery(
  queryObjectOriginal,
  onProgressCallback,
  fetchedFilteractionIds = [],
  showSystemLoader,
  skipMetadata = false
) {
  const queryObject = { ...queryObjectOriginal }
  // Loop recursivo entre todos os objetos da query
  for (const key of Object.keys(queryObject)) {
    if (key === '$in' || key === '$nin') {
      // Nada a fazer
    } else if (Array.isArray(queryObject[key])) {
      // Se for um array ($and/$or)
      for (const index in queryObject[key]) {
        queryObject[key][index] = await resolveQuery(
          queryObject[key][index],
          onProgressCallback,
          fetchedFilteractionIds,
          showSystemLoader,
          skipMetadata
        )
      }
    } else if (queryObject[key] instanceof Object) {
      // Se for um objeto
      queryObject[key] = await resolveQuery(
        queryObject[key],
        onProgressCallback,
        fetchedFilteractionIds,
        showSystemLoader,
        skipMetadata
      )
    } else {
      // Se chegou aqui é uma primativa (strings, inteiros, etc)
      if (key === '$filteraction') {
        // Se for uma primativa com chave $filteraction, significa que temos
        // um UUID de uma filteraction, que será consultado no backend,
        // e sua query será resolvida
        const filteractionId = queryObject[key]
        if (fetchedFilteractionIds.includes(filteractionId)) {
          queryObject[key] = '__CYCLIC_DEPENDENCY_DETECTED__'
          publishProgress(
            onProgressCallback,
            'Cyclic dependency detected on Filteraction ID ' + filteractionId
          )
        } else {
          fetchedFilteractionIds.push(filteractionId)
          publishProgress(
            onProgressCallback,
            'Fetching Filteraction ID ' + filteractionId
          )
          const filteraction = await fetchFilteraction(
            filteractionId,
            showSystemLoader,
            skipMetadata
          )
          publishProgress(
            onProgressCallback,
            'Finished fetching Filteraction ID ' + filteractionId
          )
          const query = JSON.parse(filteraction.query)
          const filteractionResolvedQuery = await resolveQuery(
            query,
            onProgressCallback,
            fetchedFilteractionIds,
            showSystemLoader,
            skipMetadata
          )
          delete queryObject[key]
          const objectclassId = filteraction.filter_action_objectclass[0].uuid
          publishProgress(
            onProgressCallback,
            'Executing query on Objectclass ID ' + objectclassId
          )
          const objectsResponse = await executeQuery({
            objectclass: objectclassId,
            query: filteractionResolvedQuery,
            showSystemLoader,
            skipMetadata,
            projectFields: {
              uuid: 1
            }
          })
          publishProgress(
            onProgressCallback,
            'Finished executing query on Objectclass ID ' + objectclassId
          )
          queryObject.$in = objectIds(objectsResponse.data.objects)
        }
      }
    }
  }
  return { ...queryObject }
}

// fetchFilteraction obtém o objeto de uma Filteraction
// a partir de seu UUID
export async function fetchFilteraction(
  filteractionId,
  showSystemLoader,
  skipMetadata = false
) {
  // A variável window.idObjectclassFilteraction é alimentada pelo config.js
  const response = await executeQuery({
    objectclass: window.idObjectclassFilteraction,
    query: {
      'uuid.des': { $eq: filteractionId }
    },
    showSystemLoader,
    skipMetadata,
    projectFields: {
      uuid: 1,
      query: 1,
      filter_action_objectclass: 1
    }
  })
  return await response.data.objects[0]
}

// executeQuery recebe uma objectclassId e uma query, executa
// o fetch no backend, e retorna os IDs dos objetos retornados
async function executeQuery({
  objectclass,
  query,
  showSystemLoader,
  orderby = [],
  skipMetadata = false,
  projectFields
}) {
  const hasProjectFields = typeof projectFields !== 'undefined'

  return await postObjectsFilter(
    {
      query,
      objectclass,
      orderby,
      pagination: {},
      skip_metadata: skipMetadata,
      ...(hasProjectFields ? { project_fields: projectFields } : {})
    },
    showSystemLoader
  )
}

function objectIds(objectArray) {
  return objectArray.map((i) => i.id || i.uuid)
}

function publishProgress(callback, message) {
  if (typeof callback === 'function') {
    callback(message)
  }
}
